import yaml from 'js-yaml';
import { ZodError } from 'zod';
import {
  EnvironmentLogic,
  ExpressionLogic,
  INTENT_DEFINITIONS,
  PRIMITIVE_NAMES,
  PRIMITIVE_NAMES_SET,
  RECIPIENT_DEFINITIONS,
  TOKEN_RE,
  type Environment,
  type ShareFacts,
  type IntentFacts,
} from '@/modules/form/lang';
import type { Topic, TopicTypeId } from '@/modules/topic/types';
import type { Contact } from '@/modules/contact/types';
import type {
  BodyWrap,
  ContextVars,
  FieldValueMap,
  MessageIntent,
  PrimitiveName,
  TemplateSet,
  TemplateField,
  Template,
} from './types';
import { OTHER_OPTION } from './types';
import { intentGroupOf, TemplatesFileSchema } from './schema';

/**
 * The facts `TemplateLogic.buildContext` takes beyond the topic, the
 * recipients and the sender: the ones it cannot derive itself.
 */
export type SuppliedFacts = Partial<
  Omit<
    ShareFacts,
    'topic' | 'now' | 'profileName' | 'isUpdateFlow' | 'updateSummary'
  >
> &
  IntentFacts;

// TOKEN_RE with one optional preceding space captured, so an empty
// expansion can swallow it (see expandPrimitives).
const SPACE_AND_TOKEN_RE = new RegExp(`( ?)${TOKEN_RE.source}`, 'g');

/**
 * The option-text expansion rule, over whatever a `{token}` is allowed to mean
 * here. `resolve` is asked for each token's value and returns null to leave it
 * as the author wrote it.
 *
 * A token that resolves to '' also swallows one immediately preceding SPACE,
 * so an option with an interior token — the singleton greeting
 * "Hi {_recipientInformalName}," — collapses to "Hi," (not "Hi ,") for a
 * recipient with no usable name. Only a plain space is swallowed, never a
 * newline: paragraph-owning tokens (e.g. {_whatsNewIfUpdating}) manage their
 * own leading breaks and must keep them.
 */
function expandTokens(
  text: string,
  resolve: (name: string) => string | null
): string {
  return text.replace(
    SPACE_AND_TOKEN_RE,
    (match, space: string, source: string) => {
      const value = ExpressionLogic.evaluateSource(source, resolve);
      if (value === null) return match;
      return value === '' ? '' : space + value;
    }
  );
}

/**
 * Replace embedded `{_name}` occurrences (known primitive names only) with
 * their resolved values from `ctx`, leaving everything else untouched. A no-op
 * for strings without primitives, so plain literal options pass through
 * unchanged.
 */
function expandPrimitives(text: string, ctx: ContextVars): string {
  return expandTokens(text, name =>
    PRIMITIVE_NAMES_SET.has(name) ? (ctx[name as PrimitiveName] ?? '') : null
  );
}

/**
 * Test escape hatch: E2E tests inject a stable YAML via
 * `window.__TEST_TEMPLATES_YAML__` (set in `Cypress.on('window:before:load')`)
 * so the production template files can evolve without breaking the wizard
 * walk-through assertions in Cypress. When set, it stands in for every file
 * declaring the same intents the test file does — which, now that every file
 * declares the same three, is all of them.
 */
type TestWindow = Window & { __TEST_TEMPLATES_YAML__?: string };
const testOverride: string | undefined =
  typeof window !== 'undefined'
    ? (window as TestWindow).__TEST_TEMPLATES_YAML__ || undefined
    : undefined;

/**
 * Parse + fully validate a templates YAML string (the standard files and custom
 * overrides share the same schema), returning frozen, immutable template sets.
 * Throws on invalid YAML or schema violations.
 */
function parseTemplates(yamlSource: string): readonly TemplateSet[] {
  const parsed = TemplatesFileSchema.parse(yaml.load(yamlSource));
  const wrapByIntent = new Map<MessageIntent, BodyWrap>(
    parsed.bodyWraps.map(w => [w.intent, w])
  );
  return Object.freeze(
    parsed.tones.map(t =>
      Object.freeze({
        ...t,
        templates: Object.freeze(
          t.templates.map(tmpl => {
            const wrap = wrapByIntent.get(tmpl.intent);
            // `body` (a master-authored literal) wins over `wrappedBody`; the
            // schema guarantees at least one is present and that a `wrappedBody`
            // has a matching wrap.
            const usesWrapped =
              tmpl.body === undefined && tmpl.wrappedBody !== undefined;
            const body = usesWrapped
              ? `${wrap?.prefix ?? ''}${tmpl.wrappedBody}${wrap?.suffix ?? ''}`
              : (tmpl.body ?? '');
            const resolved: Template = {
              intent: tmpl.intent,
              body,
              fields: Object.freeze(tmpl.fields),
              ...(wrap ? { wrap } : {}),
              ...(usesWrapped ? { wrappedBody: tmpl.wrappedBody } : {}),
            };
            return Object.freeze(resolved);
          })
        ),
      })
    )
  );
}

const allTemplateYamls = import.meta.glob<string>(
  '/src/data/*_templates.yaml',
  {
    query: '?raw',
    import: 'default',
    eager: true,
  }
);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The intent group a templates file declares, read from its `bodyWraps`. */
function fileIntentGroup(yamlSource: string): readonly MessageIntent[] | null {
  const doc = yaml.load(yamlSource);
  const wraps =
    isRecord(doc) && Array.isArray(doc.bodyWraps) ? doc.bodyWraps : [];
  return intentGroupOf(
    wraps.flatMap(w =>
      isRecord(w) && typeof w.intent === 'string' ? [w.intent] : []
    )
  );
}

const overrideIntents = testOverride ? fileIntentGroup(testOverride) : null;

const templatesByTopicTypeId: Record<string, readonly TemplateSet[]> = {};
const rawYamlByTopicTypeId: Record<string, string> = {};
for (const [path, yamlSource] of Object.entries(allTemplateYamls)) {
  const match = path.match(/\/([a-z]+)_templates\.yaml$/);
  if (!match) continue;
  const source =
    testOverride &&
    overrideIntents !== null &&
    overrideIntents === fileIntentGroup(yamlSource)
      ? testOverride
      : yamlSource;
  templatesByTopicTypeId[match[1]] = parseTemplates(source);
  rawYamlByTopicTypeId[match[1]] = source;
}

/**
 * Turn a parse/validation failure into a single readable message for the
 * editor: Zod issues are listed with their paths; js-yaml and other errors fall
 * back to their message.
 */
function formatTemplateError(err: unknown): string {
  if (err instanceof ZodError) {
    return err.issues
      .map(issue => {
        const path = issue.path.join('.');
        return path ? `${path}: ${issue.message}` : issue.message;
      })
      .join('\n');
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

/**
 * Canonical singular/plural noun for each intent, shared across the app (share
 * wizard badges, the template editor headings, the sharing-form builder's tabs).
 */
export const INTENT_NOUN: Record<MessageIntent, { one: string; many: string }> =
  {
    intro: { one: 'Intro', many: 'Intros' },
    reminder: { one: 'Reminder', many: 'Reminders' },
    update: { one: 'Update', many: 'Updates' },
  };

/** The emoji that stands for each intent wherever intents sit side by side. */
export const INTENT_SYMBOL: Record<MessageIntent, string> = {
  intro: '👋',
  reminder: '🔔',
  update: '🔄',
};

// `lineWidth: 40` makes js-yaml fold long scalars (notably a template's
// `wrappedBody`/`body`) into a chomped folded block (`>-`) wrapped to ~40
// columns, instead of emitting one long single-line quoted scalar. This keeps
// the body editable on a narrow (mobile) screen and matches how the default
// `*_templates.yaml` files are authored. It has no effect on the rest of the
// document: `options` are already block sequences and the `bodyWraps` suffix is
// already a literal block (it contains real newlines), so only over-long
// single-line scalars change shape.
const TEMPLATE_DUMP_OPTS: yaml.DumpOptions = {
  indent: 2,
  lineWidth: 40,
  noRefs: true,
};

/** Per-template payload (one of body/wrappedBody, plus optional fields). */
type TemplateRecord = {
  body?: unknown;
  wrappedBody?: unknown;
  fields?: unknown;
};

/** Result shape shared by the YAML-assembly helpers. */
type AssembleResult =
  | { ok: true; yaml: string }
  | { ok: false; error: string; conflict?: boolean };

/**
 * Reassemble a complete templates file (`version` + `bodyWraps` + `tones`) from
 * the parsed scaffolding and per-template records, then fully validate it.
 * Shared by `templatesToYaml` (master variant) and `defaultVariantToYaml`.
 */
function assembleTemplatesYaml(
  templateSets: readonly TemplateSet[],
  bodyWraps: readonly BodyWrap[],
  records: readonly (readonly TemplateRecord[])[]
): AssembleResult {
  const tonesOut = templateSets.map((templateSet, i) => {
    const templatesOut = templateSet.templates.map((template, j) => {
      const rec = records[i]?.[j] ?? {};
      const out: Record<string, unknown> = { intent: template.intent };
      if (rec.body !== undefined) out.body = rec.body;
      if (rec.wrappedBody !== undefined) out.wrappedBody = rec.wrappedBody;
      if (rec.fields !== undefined) out.fields = rec.fields;
      return out;
    });
    const tone: Record<string, unknown> = {
      id: templateSet.id,
      name: templateSet.name,
    };
    if (templateSet.icon !== undefined) tone.icon = templateSet.icon;
    tone.templates = templatesOut;
    return tone;
  });

  const wrapsOut = bodyWraps.map(w => ({
    intent: w.intent,
    prefix: w.prefix,
    suffix: w.suffix,
  }));

  const fullYaml = yaml.dump(
    { version: 1, bodyWraps: wrapsOut, tones: tonesOut },
    TEMPLATE_DUMP_OPTS
  );
  const validation = TemplateLogic.validateTemplateYaml(fullYaml);
  if (!validation.ok) {
    return { ok: false, error: validation.error };
  }
  return { ok: true, yaml: fullYaml };
}

export const TemplateLogic = {
  /** Type guard: is this wire intent one of the two News intents (#447)? */
  getTemplateSetsForTopicType(typeId: TopicTypeId): readonly TemplateSet[] {
    return templatesByTopicTypeId[typeId] ?? templatesByTopicTypeId['event'];
  },

  /**
   * Raw default templates YAML (the entire file, all tones + intents) for an
   * topic type, falling back to `event`. Used to seed the customize editor and
   * to restore the defaults on "Revert to default".
   */
  getDefaultTemplateYaml(typeId: TopicTypeId): string {
    return rawYamlByTopicTypeId[typeId] ?? rawYamlByTopicTypeId['event'];
  },

  /** Singular noun label for an intent (e.g. `reminder` → "Reminder"). */
  intentLabel(intent: MessageIntent): string {
    return INTENT_NOUN[intent].one;
  },

  /**
   * The per-file `bodyWraps` (prefix/suffix per intent), recovered from the
   * parsed template sets. Used to seed the master variant's wrap editors and to
   * supply the default variant's reassembly.
   */
  bodyWrapsOf(templateSets: readonly TemplateSet[]): readonly BodyWrap[] {
    const byIntent = new Map<MessageIntent, BodyWrap>();
    for (const set of templateSets) {
      for (const t of set.templates) {
        if (t.wrap && !byIntent.has(t.intent)) {
          byIntent.set(t.intent, t.wrap);
        }
      }
    }
    return Array.from(byIntent.values());
  },

  /**
   * Dump a single message template's editable content for the master editor —
   * its `wrappedBody` (default files) or literal `body` (custom), plus
   * `fields` — as dedented YAML (top-level indentation), so the user edits it
   * without seeing where it nests in the full file.
   */
  templateToYaml(template: Template): string {
    const obj: Record<string, unknown> =
      template.wrappedBody !== undefined
        ? { wrappedBody: template.wrappedBody }
        : { body: template.body };
    if (template.fields && template.fields.length > 0) {
      obj.fields = template.fields;
    }
    return yaml.dump(obj, TEMPLATE_DUMP_OPTS);
  },

  /** Dump just a template's `fields` as dedented YAML for the default editor. */
  fieldsToYaml(fields: readonly TemplateField[]): string {
    return yaml.dump(fields ?? [], TEMPLATE_DUMP_OPTS);
  },

  /**
   * Reconstruct a complete templates file from the parsed scaffolding plus the
   * master variant's per-template YAML texts and edited `bodyWraps`. Each
   * `texts[i][j]` may carry `wrappedBody` OR `body` (+ optional `fields`);
   * setting both is rejected (the modal surfaces that as a toast). Validated
   * before returning.
   */
  templatesToYaml(
    templateSets: readonly TemplateSet[],
    texts: readonly (readonly string[])[],
    bodyWraps: readonly BodyWrap[]
  ): AssembleResult {
    const records: TemplateRecord[][] = [];
    for (let i = 0; i < templateSets.length; i++) {
      const templateSet = templateSets[i];
      const row = texts[i] ?? [];
      const recRow: TemplateRecord[] = [];
      for (let j = 0; j < templateSet.templates.length; j++) {
        const template = templateSet.templates[j];
        const label = `${templateSet.name} – ${INTENT_NOUN[template.intent].one}`;
        const text = row[j];
        if (text === undefined) {
          return { ok: false, error: `Missing template for ${label}.` };
        }
        let parsed: unknown;
        try {
          parsed = yaml.load(text);
        } catch (err) {
          return { ok: false, error: `${label}: ${formatTemplateError(err)}` };
        }
        if (!isRecord(parsed)) {
          return {
            ok: false,
            error: `${label}: expected "wrappedBody" or "body" and optional "fields".`,
          };
        }
        if (parsed.body !== undefined && parsed.wrappedBody !== undefined) {
          return {
            ok: false,
            conflict: true,
            error: `${label}: set either "body" or "wrappedBody", not both.`,
          };
        }
        const rec: TemplateRecord = {};
        if (parsed.body !== undefined) rec.body = parsed.body;
        if (parsed.wrappedBody !== undefined)
          rec.wrappedBody = parsed.wrappedBody;
        if (parsed.fields !== undefined) rec.fields = parsed.fields;
        recRow.push(rec);
      }
      records.push(recRow);
    }
    return assembleTemplatesYaml(templateSets, bodyWraps, records);
  },

  /**
   * Reconstruct a complete templates file for the default (non-master) variant:
   * each template's edited middle becomes its `wrappedBody`, paired with the
   * edited `fields` YAML, under the file's (unedited) `bodyWraps`.
   */
  defaultVariantToYaml(
    templateSets: readonly TemplateSet[],
    bodyWraps: readonly BodyWrap[],
    wrappedTexts: readonly (readonly string[])[],
    fieldsTexts: readonly (readonly string[])[]
  ): AssembleResult {
    const records: TemplateRecord[][] = [];
    for (let i = 0; i < templateSets.length; i++) {
      const templateSet = templateSets[i];
      const recRow: TemplateRecord[] = [];
      for (let j = 0; j < templateSet.templates.length; j++) {
        const template = templateSet.templates[j];
        const label = `${templateSet.name} – ${INTENT_NOUN[template.intent].one}`;
        const rec: TemplateRecord = {
          wrappedBody: wrappedTexts[i]?.[j] ?? '',
        };
        const fieldsText = fieldsTexts[i]?.[j];
        if (fieldsText !== undefined && fieldsText.trim() !== '') {
          let parsedFields: unknown;
          try {
            parsedFields = yaml.load(fieldsText);
          } catch (err) {
            return {
              ok: false,
              error: `${label} fields: ${formatTemplateError(err)}`,
            };
          }
          rec.fields = parsedFields;
        } else {
          rec.fields = [];
        }
        recRow.push(rec);
      }
      records.push(recRow);
    }
    return assembleTemplatesYaml(templateSets, bodyWraps, records);
  },

  /**
   * Parse + fully validate a templates YAML string into immutable template sets.
   * Used to render an topic's custom templates and to seed the editor.
   */
  parseTemplates,

  /**
   * Validate a templates YAML string for the editor, returning a readable error
   * message instead of throwing. `ok: true` means the YAML is well-formed and
   * passes the full template schema.
   */
  validateTemplateYaml(
    yamlSource: string
  ): { ok: true } | { ok: false; error: string } {
    try {
      parseTemplates(yamlSource);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatTemplateError(err) };
    }
  },

  getTemplate(t: TemplateSet, intent: MessageIntent): Template {
    const template = t.templates.find(i => i.intent === intent);
    if (!template) {
      throw new Error(`Unknown template intent: "${t.id}/${intent}"`);
    }
    return template;
  },

  /**
   * Render a plain template string (no user fields) by expanding its
   * primitives — e.g. the impression-goal prose a form's `goal` slot carries.
   */
  renderText(text: string, ctx: ContextVars): string {
    return expandPrimitives(text, ctx);
  },

  /**
   * `renderText` over a wider vocabulary: the same expansion and the same
   * swallowed space, but the caller says what each `{token}` stands for and
   * returns null for one it does not recognise. The garden's option text uses
   * it to resolve a variable alongside the primitives, which it can do
   * because a slot can reach the rest of its form.
   */
  expandTokens(text: string, resolve: (name: string) => string | null): string {
    return expandTokens(text, resolve);
  },

  /**
   * A field's label as the sender should read it. Every end-user-facing surface
   * that shows one — the compose preview's stand-in for an unfilled field, the
   * field editor's heading — goes through here, so a label that embeds a
   * primitive reaches the sender as its value and never as raw braces.
   *
   * Untrimmed: an `optional` slot's label carries the offered text's own
   * leading and trailing spacing, which is what lets the stand-in sit exactly
   * where that text would in the sentence.
   */
  fieldLabel(field: TemplateField, ctx: ContextVars): string {
    return expandPrimitives(field.label, ctx);
  },

  /** All fields the user can fill (free text or chip pick). */
  getUserFields(t: Template) {
    return t.fields;
  },

  getUserFieldIds(t: Template): readonly string[] {
    return TemplateLogic.getUserFields(t).map(f => f.id);
  },

  /** User fields ordered by their first appearance in the template body. */
  getUserFieldsInBodyOrder(t: Template): readonly TemplateField[] {
    const fieldsById = new Map(t.fields.map(field => [field.id, field]));
    return ExpressionLogic.freeNames(
      ExpressionLogic.template(t.body, 'asWritten')
    ).flatMap(name => {
      const field = name.startsWith('_') ? undefined : fieldsById.get(name);
      return field ? [field] : [];
    });
  },

  /**
   * User fields whose value is missing or whitespace-only.
   *
   * When `ctx` is supplied, also flags fields whose stored value is a
   * primitive option (e.g. `_profileName`) that resolves to an empty
   * string — so a blank profile name doesn't silently pass as "filled".
   *
   * `singletonMode` says whether each message reaches exactly one person, which
   * decides which options a field actually offers (see `isVestigialField`).
   */
  getMissingFieldIds(
    t: Template,
    values: FieldValueMap,
    ctx?: ContextVars,
    singletonMode = true
  ): readonly string[] {
    return TemplateLogic.getUserFieldIds(t).filter(id => {
      const field = t.fields.find(f => f.id === id);
      // An optional field (an inline `checkbox` slot) is satisfied either way —
      // leaving it unchecked is a choice, not an omission.
      if (field?.optional) return false;
      // A field whose every offered option resolves to blank contributes
      // nothing to the message and is never shown, so it cannot be waiting on
      // the user.
      if (
        ctx &&
        field &&
        TemplateLogic.isVestigialField(field, ctx, singletonMode)
      ) {
        return false;
      }
      const raw = (values[id] ?? '').trim();
      if (!raw) return true;
      if (!ctx) return false;
      const selectedOption = field
        ? TemplateLogic.allOptions(field).find(
            o => o !== OTHER_OPTION && o === raw
          )
        : undefined;
      return (
        selectedOption !== undefined &&
        !TemplateLogic.resolveOptionValue(selectedOption, ctx).trim()
      );
    });
  },

  isComplete(
    t: Template,
    values: FieldValueMap,
    ctx?: ContextVars,
    singletonMode = true
  ): boolean {
    return (
      TemplateLogic.getMissingFieldIds(t, values, ctx, singletonMode).length ===
      0
    );
  },

  /**
   * True iff nothing this field offers in the current mode can add anything to
   * the message, so there is no question worth asking. Two ways to get there:
   * every offered option resolves to blank (a singleton-only value like
   * `_recipientInformalName` for a nameless recipient), or this mode offers no
   * options at all — an optional slot restricted to `dm` in a group text, whose only
   * options are singleton-mode ones. Such a field is hidden rather than shown
   * as an unanswerable choice. A free-text ("Other") alternative always keeps
   * the field alive, since the user can still type something; so does a field
   * that declares no options anywhere, which is a free-text fill-in.
   *
   * `singletonMode` — whether each message reaches exactly one person — decides
   * which options count, exactly as it does for the picker (`editorOptions`).
   * It defaults to true, the permissive reading, so a caller that cannot know
   * the mode judges the field on everything it could ever offer.
   */
  isVestigialField(
    field: TemplateField,
    ctx: ContextVars,
    singletonMode = true
  ): boolean {
    if (TemplateLogic.fieldAllowsOther(field)) return false;
    if (TemplateLogic.allOptions(field).length === 0) return false;
    return TemplateLogic.editorOptions(field, singletonMode).every(
      option => !TemplateLogic.resolveOptionValue(option, ctx).trim()
    );
  },

  /** True iff the field's `options` contains the OTHER sentinel. */
  fieldAllowsOther(field: TemplateField): boolean {
    return !!field.options && field.options.includes(OTHER_OPTION);
  },

  /**
   * All selectable option values for a field, the addressing-mode-specific
   * ones first. Used wherever a stored value must be matched back to its
   * declared option (composition, preview) regardless of mode.
   */
  allOptions(field: TemplateField): readonly string[] {
    return [
      ...(field.optionsSingletonMode ?? []),
      ...(field.optionsGroupMode ?? []),
      ...(field.options ?? []),
    ];
  },

  /**
   * Options offered in the picker UI. A field may restrict options to one
   * addressing mode: singleton-mode options (which may embed
   * `{_recipientInformalName}`) are only offered where each message has
   * exactly one recipient so the name resolves correctly, and group-mode
   * options only where it has several. Options belonging to neither are
   * offered in both.
   */
  editorOptions(
    field: TemplateField,
    singletonMode: boolean
  ): readonly string[] {
    return [
      ...(singletonMode
        ? (field.optionsSingletonMode ?? [])
        : (field.optionsGroupMode ?? [])),
      ...(field.options ?? []),
    ];
  },

  /**
   * True when `value` is a declared option that the current mode no longer
   * offers — e.g. a singleton-only greeting ("Hey {_recipientInformalName}")
   * picked in singleton mode, then the user switched to group texts. Such a
   * value must be dropped so the user re-picks from the offered options.
   * Custom "Other" free text is not a declared option, so it is never stale.
   */
  isStaleOptionValue(
    field: TemplateField,
    value: string,
    singletonMode: boolean
  ): boolean {
    if (!value) return false;
    if (TemplateLogic.editorOptions(field, singletonMode).includes(value)) {
      return false;
    }
    return TemplateLogic.allOptions(field).includes(value);
  },

  /** Resolve primitive option labels/values against the current context. */
  resolveOptionValue(option: string, ctx: ContextVars): string {
    // Trim so an option ending in an empty primitive collapses cleanly —
    // e.g. the singleton greeting "Hey {_recipientInformalName}" for a nameless
    // recipient renders "Hey", not "Hey " (which left a stray space before the
    // template's comma: "Hey , …"). Applies to preview and composed SMS alike.
    return expandPrimitives(option, ctx).trim();
  },

  /**
   * What picking `option` on `field` contributes to the message. An `optional`
   * slot's option keeps its own spacing — the leading space in
   * `" {_recipientInformalName}"` is what separates it from the word before —
   * while every other option is trimmed (see `resolveOptionValue`). Shared by
   * `composeBody` and the compose preview, so the preview spaces the sentence
   * exactly the way the sent message will.
   */
  optionText(
    field: TemplateField | undefined,
    option: string,
    ctx: ContextVars
  ): string {
    return field?.optional
      ? expandPrimitives(option, ctx)
      : TemplateLogic.resolveOptionValue(option, ctx);
  },

  /**
   * The whitespace a composed body settles into: runs of spaces and tabs
   * after a word collapse to one (a line's indent is kept), none is left
   * before punctuation, a line never ends in one, three or more newlines become a
   * single blank line, and the whole thing is trimmed. It is what lets a field
   * or a slot contribute nothing and leave the words around it spaced as if it
   * had never been there — so every path that composes a body finishes here,
   * and they all space a sentence the same way.
   */
  tidyBody(body: string): string {
    return ExpressionLogic.tidyText(body)
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  },

  /**
   * What `{name}` resolves to in a classic body: a primitive is its value for
   * this message, and every field is implicitly a variable named after its id,
   * holding what it contributes (see `optionText`) or the sender's own words.
   * Null for a name the template does not declare.
   */
  resolver(
    t: Template,
    values: FieldValueMap,
    ctx: ContextVars
  ): (name: string) => string | null {
    const fieldsById = new Map(t.fields.map(field => [field.id, field]));
    return name => {
      if (PRIMITIVE_NAMES_SET.has(name)) {
        return ctx[name as PrimitiveName] ?? '';
      }
      const field = fieldsById.get(name);
      if (!field) return null;
      const value = values[name] ?? '';
      const selectedOption = TemplateLogic.allOptions(field).find(
        option => option !== OTHER_OPTION && option === value
      );
      return selectedOption === undefined
        ? value
        : TemplateLogic.optionText(field, selectedOption, ctx);
    };
  },

  /**
   * Substitute every {token} in template.body. Throws on unknown tokens
   * (defense-in-depth — the schema should prevent this for valid YAML).
   */
  composeBody(t: Template, values: FieldValueMap, ctx: ContextVars): string {
    const resolve = TemplateLogic.resolver(t, values, ctx);
    const substituted = t.body.replace(TOKEN_RE, (_match, source: string) => {
      const value = ExpressionLogic.evaluateSource(source, resolve);
      if (value === null) throw new Error(`Unknown token {${source}}`);
      return value;
    });
    return TemplateLogic.tidyBody(substituted);
  },

  /**
   * Every primitive as it resolves in `env`, for a classic template,
   * which reads its primitives from a map rather than an environment.
   */
  contextOf(env: Environment): ContextVars {
    return Object.fromEntries(
      PRIMITIVE_NAMES.map(name => [
        name,
        EnvironmentLogic.resolve(env, name) ?? '',
      ])
    ) as Record<PrimitiveName, string>;
  },

  /**
   * Every primitive for the given topic, recipients and now: the share,
   * intent and recipient frames defined over these facts (see
   * `form/lang/primitive.ts`), read into a map.
   */
  buildContext(
    topic: Topic,
    recipients: readonly Contact[],
    now: Date = new Date(),
    profileName = '',
    isUpdateFlow = false,
    legId?: string,
    updateSummary?: string,
    supplied: SuppliedFacts = {}
  ): ContextVars {
    const { connectionToPrevLeg, reshareStory, ...shareSupplied } = supplied;
    const shareEnv = EnvironmentLogic.shareEnvironment({
      ...shareSupplied,
      topic,
      now,
      profileName,
      isUpdateFlow,
      updateSummary,
    });
    const intentEnv = EnvironmentLogic.extendWithDefinitions(
      shareEnv,
      { connectionToPrevLeg, reshareStory },
      INTENT_DEFINITIONS
    );
    return TemplateLogic.contextOf(
      EnvironmentLogic.extendWithDefinitions(
        intentEnv,
        { contacts: recipients, legId },
        RECIPIENT_DEFINITIONS
      )
    );
  },
};
